import imageCompression from "browser-image-compression";

const COMPRESSION_THRESHOLD = 1 * 1024 * 1024; // 1MB

interface CompressionOptions {
  /** Max file size in MB (default: 0.8) */
  maxSizeMB?: number;
  /** Max width or height in pixels (default: 1920) */
  maxWidthOrHeight?: number;
  /** JPEG quality 0-1 (default: 0.7) */
  initialQuality?: number;
  /** Use web worker to avoid blocking UI (default: true) */
  useWebWorker?: boolean;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1920,
  initialQuality: 0.7,
  useWebWorker: true,
};

/** More aggressive settings for AI analysis */
const AI_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.6,
  maxWidthOrHeight: 1600,
  initialQuality: 0.6,
  useWebWorker: true,
};

function isCompressibleImage(file: File): boolean {
  const compressibleTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (compressibleTypes.includes(file.type)) return true;
  const ext = file.name.toLowerCase();
  return [".jpg", ".jpeg", ".png", ".webp"].some((e) => ext.endsWith(e));
}

function isHeicImage(file: File): boolean {
  const heicTypes = ["image/heic", "image/heif"];
  if (heicTypes.includes(file.type.toLowerCase())) return true;
  return /\.(heic|heif)$/i.test(file.name);
}

/**
 * Convert a HEIC/HEIF file to JPEG so it can be run through the normal
 * browser-image-compression pipeline (which can't decode HEIC directly).
 *
 * Output is JPEG at quality 0.8, NOT lossless PNG. heic2any runs on the main
 * thread, and a lossless PNG of a 12MP photo is ~20-40MB — writing and then
 * re-compressing that froze low-end phones for seconds per photo. A quality-0.8
 * JPEG is a fraction of the size, so the subsequent compression step (and the
 * upload) have far less to chew on.
 *
 * Best-effort: on failure, returns the original file unchanged so the caller
 * falls through to an uncompressed-but-working upload rather than failing
 * outright.
 */
async function convertHeicToJpeg(file: File): Promise<File> {
  try {
    // Dynamic import — heic2any is ~1.36MB, only load it when actually needed
    // (same lazy-load pattern as src/components/HeicImage.tsx).
    const { default: heic2any } = await import("heic2any");
    const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.8 });
    const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
    const jpegName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([jpegBlob], jpegName, { type: "image/jpeg" });
  } catch (err) {
    console.error("HEIC conversion failed, uploading original file:", err);
    return file;
  }
}

/**
 * Compress a single image if it exceeds the threshold.
 * Returns the original file if it's already small enough or not a compressible format.
 * HEIC/HEIF files (the default iPhone camera format) are converted to JPEG
 * first so they can be compressed like any other image.
 */
export async function compressImage(
  file: File,
  options?: CompressionOptions
): Promise<File> {
  const source = isHeicImage(file) ? await convertHeicToJpeg(file) : file;

  if (source.size <= COMPRESSION_THRESHOLD || !isCompressibleImage(source)) {
    return source;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };

  const compressed = await imageCompression(source, {
    maxSizeMB: opts.maxSizeMB!,
    maxWidthOrHeight: opts.maxWidthOrHeight!,
    initialQuality: opts.initialQuality!,
    useWebWorker: opts.useWebWorker!,
    fileType: "image/jpeg",
  });

  // Return as File (not Blob) to preserve the name
  return new File([compressed], source.name, { type: compressed.type });
}

/**
 * Compress multiple images in parallel.
 * Non-image files are passed through unchanged.
 */
export async function compressImages(
  files: File[],
  options?: CompressionOptions
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImage(file, options)));
}

/**
 * Compress images with aggressive settings optimized for AI analysis.
 * Prioritizes speed and small file size over image quality.
 */
export async function compressImagesForAI(files: File[]): Promise<File[]> {
  return compressImages(files, AI_OPTIONS);
}

/**
 * Compress images with standard settings for general attachment uploads
 * (recon request / vehicle note photos). Balances quality against upload size.
 */
export async function compressImagesForUpload(files: File[]): Promise<File[]> {
  return compressImages(files, DEFAULT_OPTIONS);
}
